import React, { useState } from 'react';

import classNames from 'classnames';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Tooltip from '@material-ui/core/Tooltip';
import FormHelperText from '@material-ui/core/FormHelperText';

import { ErrorMessage } from 'formik';
import Select from 'react-select';
import FormControl from '@material-ui/core/FormControl';
import IconButton from '@material-ui/core/IconButton';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import NumberFormat from 'react-number-format';
import { AddCircle, Delete } from '@material-ui/icons';
import {
  BUDGET_AMOUNT_LABEL,
  DEPOSIT_AMOUNT_LABEL,
  SELECT_LABEL,
  DNI_LABEL,
  SELECT_REGION_COMMUNE_SELECTOR,
  WAITING_OPTION_LABELS,
  NO_OPTION_LABELS
} from '../../../libraries/texts';
import {
  MAXIMUM_DNI_LENGTH,
  LIMIT_SHOW_CEDULA,
  MAXIMUM_CEDULA_LENGTH,
  MINIMUM_DNI_LENGTH,
  MAXIMUM_AMOUNT_DIGITS,
  MAXIMUM_EMAIL_LENGTH,
  MAXIMUM_ADDRESS_NUMBER_LENGTH,
  MAXIMUM_ADDRESS_LENGTH,
  ADDRESS_NORMALIZE_PREFIX,
  DOUBLE_SPACE_KEY_CODE,
  DOT_CHARACTER,
  FINGER_NORMAL_STATUS_CODE,
  FINGER_DISABLED_STATUS_CODE
} from '../../../libraries/constants';
import {
  maximumSubString,
  limitCedulaCharacters,
  getFirstIndexWithValue,
  serviceFilterOption,
  removeSpaces,
  giveFormatOnPaste
} from '../../../libraries/utils';
import { isValidMinimumDNI } from '../../../libraries/validators';
import { Option, NoOptionsMessage, Placeholder, Control, checkInPatient } from './utils';
import { calculateDVDNI, getNumber, cleanCharacters } from '../../../libraries/formatters';

const inputComponent = ({ inputRef, ...props }) => {
  return <div ref={inputRef} {...props} />;
};

const getCheckBoxField = props => {
  const { values, setFieldValue, labelPlacement, name, label, classes, disabled = false } = props;
  const checkBoxProps = {};
  if (disabled) {
    checkBoxProps.disabled = true;
  }

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={values[name]}
          onChange={event => {
            setFieldValue(name, event.target.checked);
          }}
          {...checkBoxProps}
          color="primary"
        />
      }
      className={classes.checkBoxField}
      labelPlacement={labelPlacement}
      label={label}
    />
  );
};

const showErrorMessage = (errors, touched, name, classes) => {
  return (
    <React.Fragment>
      {errors[name] && touched[name] ? (
        <div className={classes.errorMessages}>{errors[name]}</div>
      ) : (
        ''
      )}
    </React.Fragment>
  );
};
export const getSelectField = props => {
  const {
    classes,
    touched,
    errors,
    values,
    options,
    setFieldValue,
    handleBlur,
    onMenuClose,
    setFieldTouched,
    name,
    label,
    ref = null,
    isSearchable = true,
    formControlClass = classes.middleWidth,
    isClearable = true,
    extraClasses = '',
    noOptionsMessage = false,
    optionLabel = option => option.name,
    optionValue = option => option.code,
    components = {
      Control: Control(inputComponent),
      NoOptionsMessage,
      Option,
      Placeholder
    },
    inputChange = (newValue, actionMeta) => newValue
  } = props;

  if (noOptionsMessage) {
    components.NoOptionsMessage = noOptionsMessage;
  }

  const {
    onChange = option => {
      setFieldValue(name, option);
    }
  } = props;

  return (
    <FormControl
      required
      className={classNames(formControlClass, extraClasses, {
        [classes.error]: errors[name] && touched[name]
      })}
    >
      <Select
        inputId={`${name}`}
        name={`${name}`}
        ref={ref}
        classes={classes}
        textFieldProps={{
          label,
          InputLabelProps: {
            shrink: true
          }
        }}
        options={options}
        components={components}
        value={values[name]}
        onChange={onChange}
        onBlur={option => {
          handleBlur(option);

          if (option.target.value) {
            setFieldValue(name, null);
          }
          if (setFieldTouched) {
            setFieldTouched(name);
          }
        }}
        onMenuClose={onMenuClose}
        placeholder={SELECT_LABEL}
        style={{
          borderColor: errors[name] && touched[name] && 'red'
        }}
        getOptionLabel={optionLabel}
        getOptionValue={optionValue}
        onInputChange={inputChange}
        required={true}
        isClearable={isClearable}
        isSearchable={isSearchable}
      />
      {errors[name] && touched[name] ? (
        <ErrorMessage name={`${name}`} component="div" className={classes.errorMessages} />
      ) : (
        ''
      )}
    </FormControl>
  );
};

export const getInputField = props => {
  const formControlProps = {};
  const inputControlProps = {};
  let isDoubleSpace = false;
  const {
    classes,
    touched,
    errors,
    values,
    setFieldValue,
    handleBlur,
    name,
    label,
    autoFocus = false,
    type = 'text',
    changeHandler,
    extraClasses = '',
    placeholder = '',
    required = true,
    formControlClass = classes.middleWidth
  } = props;
  if (required) {
    formControlProps.required = true;
  }
  if (autoFocus) {
    inputControlProps.autoFocus = true;
  }

  const removeDotInDoubleSpace = event => {
    const { keyCode } = event;
    const finalCharacter = event.target.value.slice(-1);
    if (keyCode === DOUBLE_SPACE_KEY_CODE && finalCharacter === DOT_CHARACTER) {
      isDoubleSpace = true;
    }
    return event;
  };
  return (
    <FormControl
      {...formControlProps}
      className={classNames(formControlClass, extraClasses, {
        [classes.error]: errors[name] && touched[name] && required
      })}
    >
      <InputLabel shrink={true} htmlFor={`${name}`}>
        {label}
      </InputLabel>
      <Input
        id={`${name}`}
        name={`${name}`}
        value={values[name]}
        onBlur={handleBlur}
        type={type}
        autoComplete="off"
        onKeyDown={removeDotInDoubleSpace}
        onChange={event => {
          let {
            target: { value }
          } = event;
          if (isDoubleSpace) {
            isDoubleSpace = false;
            const pos = value.lastIndexOf('.');
            if (pos >= 0) {
              value = value.substring(0, pos) + ' ';
            }
          }
          if (changeHandler) {
            value = changeHandler(value);
          }
          value = removeSpaces(value);
          setFieldValue(name, value);
        }}
        {...inputControlProps}
      />
      {!placeholder ? (
        showErrorMessage(errors, touched, name, classes)
      ) : (
        <React.Fragment>
          {errors[name] && touched[name] ? (
            <React.Fragment>
              {showErrorMessage(errors, touched, name, classes)}
              {errors[name] !== placeholder ? (
                <FormHelperText id={`${name}`} className={classes.errorSimilarMessages}>
                  {placeholder}
                </FormHelperText>
              ) : (
                ''
              )}
            </React.Fragment>
          ) : (
            <FormHelperText id={`${name}`} className={classes.errorSimilarMessages}>
              {placeholder}
            </FormHelperText>
          )}
        </React.Fragment>
      )}
    </FormControl>
  );
};

export const getFormatNumber = props => {
  const {
    classes,
    touched,
    errors,
    values,
    setFieldValue,
    handleBlur,
    name,
    prefix,
    label,
    format,
    mask,
    onKeyDown = null,
    maxDigitLimit,
    thousandSeparator = '.',
    allowEmptyFormatting = false,
    required = true,
    formControlClass = classes.middleWidth
  } = props;

  const propsValues = {};
  const formControlProps = {};
  if (format) {
    propsValues.format = format;
  }
  if (mask) {
    propsValues.mask = mask;
  }
  if (required) {
    formControlProps.required = required;
  }

  return (
    <FormControl
      {...formControlProps}
      className={classNames(formControlClass, {
        [classes.error]: errors[name] && touched[name] && required
      })}
    >
      <InputLabel shrink={true} htmlFor={`${name}`}>
        {label}
      </InputLabel>
      <NumberFormat
        id={`${name}`}
        prefix={prefix}
        value={values[name]}
        onBlur={handleBlur}
        thousandSeparator={thousandSeparator}
        decimalSeparator={false}
        allowNegative={false}
        customInput={Input}
        allowEmptyFormatting={allowEmptyFormatting}
        onKeyDown={onKeyDown}
        onValueChange={values => {
          let { value } = values;
          value = parseInt(value);
          const valueString = `${value}`;
          if (maxDigitLimit && maxDigitLimit < valueString.length) {
            value = valueString.substring(0, maxDigitLimit);
          }
          setFieldValue(name, value);
        }}
        {...propsValues}
      />
      <ErrorMessage name={`${name}`} component="div" className={classes.errorMessages} />
    </FormControl>
  );
};

export const getPasswordInputField = props => {
  const { label, prefixLabel, placeholder } = props;

  const commonPassword = {
    ...props,
    type: 'password',
    label: `${prefixLabel}${label}`,
    placeholder
  };
  const confirmPassword = {
    ...commonPassword,
    name: `confirmPassword`,
    label: `Confirmar ${label}`,
    placeholder: ''
  };

  return (
    <React.Fragment>
      {getInputField(commonPassword)}
      {getInputField(confirmPassword)}
    </React.Fragment>
  );
};
export const getWarrantyField = props => {
  let { guaranteeTypes } = props;

  const message = getMessageSelect({ ...props, options: guaranteeTypes });
  const noOptionsMessage = props => NoOptionsMessage({ ...props, text: message });

  return getSelectField({
    ...props,
    name: 'guaranteeType',
    label: 'Tipo de garantía *',
    optionLabel: option => option.name.toUpperCase(),
    options: guaranteeTypes,
    noOptionsMessage
  });
};

export const getMedicalRiskField = props => {
  const { risks } = props;
  const message = getMessageSelect({ ...props, options: risks });
  const noOptionsMessage = props => NoOptionsMessage({ ...props, text: message });

  return getSelectField({
    ...props,
    name: 'medicalRisk',
    label: 'Riesgo médico *',
    optionLabel: option => option.name.toUpperCase(),
    options: risks,
    noOptionsMessage
  });
};

export const getBudgetAmountField = props => {
  const onKeyDown = event => {
    if (event.target.value.includes(' 0')) {
      event.target.value = event.target.value.replace(' 0', ' ');
    }
  };
  return getFormatNumber({
    ...props,
    name: 'budgetAmount',
    prefix: '$ ',
    required: true,
    onKeyDown,
    maxDigitLimit: MAXIMUM_AMOUNT_DIGITS,
    allowEmptyFormatting: true,
    label: BUDGET_AMOUNT_LABEL
  });
};

export const getAmountField = props => {
  const {
    classes,
    touched,
    errors,
    values,
    setFieldValue,
    handleBlur,
    format,
    mask,
    formControlClass = classes.middleWidth
  } = props;
  const name = 'amount';
  const required = false;
  const prefix = '$ ';
  const maxDigitLimit = MAXIMUM_AMOUNT_DIGITS;
  const allowEmptyFormatting = true;
  const label = DEPOSIT_AMOUNT_LABEL;

  const propsValues = {};
  const formControlProps = {};
  if (format) {
    propsValues.format = format;
  }
  if (mask) {
    propsValues.mask = mask;
  }
  if (required) {
    formControlProps.required = required;
  }

  return (
    <FormControl
      {...formControlProps}
      className={classNames(formControlClass, {
        [classes.error]: errors[name] && touched[name]
      })}
    >
      <InputLabel shrink={true} htmlFor={`${name}`}>
        {label}
      </InputLabel>
      <NumberFormat
        id={`${name}`}
        prefix={prefix}
        value={values[name]}
        onBlur={handleBlur}
        thousandSeparator={'.'}
        decimalSeparator={false}
        allowNegative={false}
        customInput={Input}
        allowEmptyFormatting={allowEmptyFormatting}
        onKeyDown={event => {
          if (event.target.value.includes(' 0')) {
            event.target.value = event.target.value.replace(' 0', ' ');
          }
          return event;
        }}
        onValueChange={values => {
          let { value } = values;
          value = parseInt(value);
          const valueString = `${value}`;
          if (maxDigitLimit && maxDigitLimit < valueString.length) {
            value = valueString.substring(0, maxDigitLimit);
          }
          setFieldValue(name, value);
        }}
        {...propsValues}
      />
      <ErrorMessage name={`${name}`} component="div" className={classes.errorMessages} />
    </FormControl>
  );
};

const getMessageSelect = props => {
  const { loading, options } = props;
  let message = loading.principalLoading ? WAITING_OPTION_LABELS : NO_OPTION_LABELS;

  if (options.length > 0) {
    message = WAITING_OPTION_LABELS;
  }
  return message;
};

export const getServicesField = props => {
  const {
    classes,
    touched,
    errors,
    values,
    services,
    setFieldValue,
    handleBlur,
    addNewService,
    removeNewService,
    allowAddService,
    components = {
      Control: Control(inputComponent),
      Option,
      Placeholder
    },
    allowRemoveService
  } = props;

  const message = getMessageSelect({ ...props, options: services });
  const noOptionsMessage = props => NoOptionsMessage({ ...props, text: message });
  components.NoOptionsMessage = noOptionsMessage;

  const [availableAdd, setAvailableAdd] = useState([false, false, false, false, false, false]);

  const minimumIndex = getFirstIndexWithValue(values.services);
  if (touched[`service-${minimumIndex}`]) {
    touched.services = true;
  }
  values.services.map((elem, index) => {
    const isNotThereValue = !(elem && elem.code);
    if (isNotThereValue === availableAdd[index]) {
      availableAdd[index] = !isNotThereValue;
      setAvailableAdd(availableAdd);
    }
    return elem;
  });

  return values.services.map((elem, index) => {
    const attributes = {};

    if (index === minimumIndex) {
      attributes.required = true;
    }
    const inputId = `service-${index.toString()}`;
    const isRequiredLabel = attributes.required ? ' *' : '';
    const pos = index + 1;
    return (
      <FormControl
        {...attributes}
        fullWidth
        key={index.toString()}
        className={classNames(classes.fullWidth, classes.serviceSelectForm, {
          [classes.error]: errors.services && touched[inputId] && attributes.required
        })}
      >
        <Select
          inputId={inputId}
          className={classes.serviceSelect}
          classes={classes}
          textFieldProps={{
            label: `Prestación${isRequiredLabel}`,
            InputLabelProps: {
              shrink: true
            }
          }}
          value={elem && elem.code ? elem : null}
          options={services}
          components={components}
          onBlur={option => {
            handleBlur(option);
            if (option.target.value) {
              values.services[index] = null;
              setFieldValue('services', values.services);
            }
          }}
          filterOption={serviceFilterOption}
          onChange={option => {
            values.services[index] = option;
            availableAdd[index] = option && option.code ? true : false;
            setAvailableAdd(availableAdd);
            setFieldValue('services', values.services);
          }}
          placeholder={SELECT_LABEL}
          getOptionLabel={option => `${option.code} ${option.name}`}
          getOptionValue={option => option.code}
          isClearable={elem && elem.code && pos === values.services.length}
          isDisabled={elem && elem.code && pos !== values.services.length}
        />

        {allowAddService(index) ? (
          <Tooltip title="Agregar" placement="bottom">
            <IconButton
              aria-label="Add"
              color="primary"
              onClick={addNewService}
              disabled={!availableAdd[index]}
            >
              <AddCircle />
            </IconButton>
          </Tooltip>
        ) : (
          <IconButton disableRipple={true} disabled={true} />
        )}
        {allowRemoveService() ? (
          <IconButton disableRipple={true} disabled={true} />
        ) : (
          <Tooltip title="Eliminar" placement="bottom">
            <IconButton aria-label="Delete" color="secondary" onClick={removeNewService(index)}>
              <Delete />
            </IconButton>
          </Tooltip>
        )}
        {isRequiredLabel ? (
          <ErrorMessage
            name="services"
            component="div"
            className={classNames(classes.errorMessages)}
          />
        ) : (
          ''
        )}
      </FormControl>
    );
  });
};
export const getDNIField = props => {
  const {
    classes,
    touched,
    errors,
    values,
    setFieldTouched,
    setFieldValue,
    handleBlur,
    name,
    label,
    setShowCedula,
    formControlClass = classes.middleWidth,
    disabled = false,
    prefix
  } = props;

  const dniName = `${name}Number`;
  const fingerStatus = `${name.replace('Dni', '')}Status`;
  const beforeDni = `${name.replace('Dni', '')}BeforeDni`;
  const fingerMessage = `${name.replace('Dni', '')}Message`;
  const cedulaName = `${name.replace('Dni', '')}Cedula`;
  const contributionField = `${name.replace('Dni', '')}Contributions`;
  const hasFingerPrint = `${name.replace('Dni', '')}CapturedFingerprint`;
  const dniAttributes = {};

  if (disabled) {
    dniAttributes.disabled = disabled;
  }

  const handleKeyDownDni = event => {
    const { key } = event;
    if (event.target.value.startsWith('0')) {
      event.target.value = event.target.value.replace('0', '');
    }

    if (key === 'Tab') {
      const value = values[name];
      if (
        !value ||
        (value && value.toString().length < MINIMUM_DNI_LENGTH) ||
        value >= LIMIT_SHOW_CEDULA ||
        !setShowCedula
      ) {
        return;
      }
      if (setShowCedula) {
        value && value < LIMIT_SHOW_CEDULA ? setShowCedula(true) : setShowCedula(false);
      }

      const elem = document.getElementById(cedulaName);
      event.preventDefault();
      event.stopPropagation();

      if (elem) {
        elem.focus();
      }
    }
  };
  const toNumberAndCheckMaximum = values => {
    let { value } = values;
    value = parseInt(value).toString();
    if (!isValidMinimumDNI(value)) {
      value = maximumSubString(MAXIMUM_DNI_LENGTH)(value);
    }
    setFieldValue(name, value);
  };
  return (
    <div className={classNames(formControlClass, classes.dniControl)}>
      <FormControl
        required
        className={classNames({
          [classes.error]: errors[name] && touched[name]
        })}
      >
        <InputLabel shrink={true} htmlFor={`${name}`}>
          {label}
        </InputLabel>
        <NumberFormat
          id={`${name}`}
          value={values[name]}
          onBlur={e => {
            handleBlur(e);
            const value = values[name];
            setFieldValue(beforeDni, value);
            const dni = calculateDVDNI(value);

            if (values[`${prefix}BeforeDni`] && values[`${prefix}BeforeDni`] === value) {
              return;
            }

            if (values[`${prefix}BeforeDni`] && values[`${prefix}BeforeDni`] !== value) {
              const fingerStatusValue = FINGER_NORMAL_STATUS_CODE;
              setFieldValue(fingerStatus, fingerStatusValue);
              setFieldValue(hasFingerPrint, false);
              setFieldValue(fingerMessage, '');
              if (values['avalDni']) {
                setFieldValue('avalHasSerialNumber', true);
                setFieldValue('avalStatus', fingerStatusValue);
              }
            }

            if (
              values['avalDni'] &&
              values['suscriptorDni'] &&
              values['avalDni'] === values['suscriptorDni']
            ) {
              const fingerStatusValue = FINGER_DISABLED_STATUS_CODE;
              setFieldValue(fingerStatus, fingerStatusValue);
              setFieldValue('avalStatus', fingerStatusValue);
              setFieldValue(hasFingerPrint, false);
              setFieldValue(fingerMessage, '');
              setFieldValue('avalHasSerialNumber', false);
              setFieldValue('suscriptorHasSerialNumber', true);
              setShowCedula(false);
              setFieldValue(dniName, dni);
              return;
            }

            if (!value || value === 'NaN' || value.toString().length < MINIMUM_DNI_LENGTH) {
              const fingerStatusValue = FINGER_DISABLED_STATUS_CODE;
              setFieldValue(dniName, '');
              setFieldValue(hasFingerPrint, false);
              setFieldValue(fingerStatus, fingerStatusValue);
              setFieldValue(fingerMessage, '');
              setFieldValue(`${prefix}HasSerialNumber`, false);
              setShowCedula(false);
              return;
            }

            if (value && value.toString().length < MINIMUM_DNI_LENGTH) {
              setFieldValue(dniName, '');
              setFieldTouched(`${cedulaName}`, false);
              setFieldValue(hasFingerPrint, false);
              if (setShowCedula) {
                setShowCedula(false);
              }
              return;
            }

            if (setShowCedula) {
              value && value < LIMIT_SHOW_CEDULA ? setShowCedula(true) : setShowCedula(false);
            }

            const fingerStatusValue =
              value && value < LIMIT_SHOW_CEDULA
                ? FINGER_NORMAL_STATUS_CODE
                : FINGER_DISABLED_STATUS_CODE;
            setFieldValue(fingerStatus, fingerStatusValue);
            setFieldValue(dniName, dni);
            setFieldValue(contributionField, []);
          }}
          thousandSeparator={'.'}
          decimalSeparator={false}
          decimalScale={false}
          allowNegative={false}
          onKeyDown={handleKeyDownDni}
          customInput={Input}
          onChange={giveFormatOnPaste}
          onValueChange={toNumberAndCheckMaximum}
          {...dniAttributes}
        />
        <ErrorMessage name={`${name}`} component="div" className={classes.errorMessages} />
      </FormControl>
      <span>
        -
        <ErrorMessage
          name={`${name}`}
          component="div"
          className={classNames(classes.errorMessages, classes.emptyError)}
        />
      </span>
      <FormControl
        required
        className={classNames(classes.dni, {
          [classes.error]: errors[dniName] && touched[dniName]
        })}
      >
        <InputLabel shrink={true} htmlFor={`${dniName}`}>
          {DNI_LABEL}
        </InputLabel>
        <Input
          id={`${dniName}`}
          value={values[dniName]}
          onBlur={handleBlur}
          disabled={true}
          onValueChange={values => {
            const { value } = values;
            setFieldValue(dniName, value);
          }}
        />
        <ErrorMessage
          name={`${name}`}
          component="div"
          className={classNames(classes.errorMessages, classes.emptyError)}
        />
      </FormControl>
    </div>
  );
};

export const getCedulaSerialNumber = props => {
  const { showCedula, values, setFieldValue, name, isMinDigitsDNI = false, prefix } = props;

  if (!showCedula && values[name] && !values[`${prefix}HasSerialNumber`]) {
    setFieldValue(name, '');
  }
  const changeHandler = value => {
    let newVal = cleanCharacters(limitCedulaCharacters(value));
    if (!newVal.includes('A')) {
      newVal = maximumSubString(MAXIMUM_CEDULA_LENGTH - 1)(newVal);
      newVal = !newVal ? '' : getNumber(newVal);
    } else {
      newVal = maximumSubString(MAXIMUM_CEDULA_LENGTH)(newVal);
    }

    return newVal;
  };

  const hasSameDni = values[`avalDni`] === values[`suscriptorDni`];

  return isMinDigitsDNI &&
    values[`${prefix}Dni`] < LIMIT_SHOW_CEDULA &&
    (values[`${prefix}HasSerialNumber`] || (showCedula && !hasSameDni))
    ? getInputField({ ...props, changeHandler, autoFocus: true })
    : '';
};

export const getRegionField = props => {
  const { handleBlur, setFieldValue, prefix, values, classes, loading } = props;
  const newHandleBlur = event => {
    const { value } = event.target;
    handleBlur(event);
    setFieldValue(`${prefix}${ADDRESS_NORMALIZE_PREFIX}`, `${value}${event.timeStamp}`);
  };

  const extraClasses = !values[`is${prefix}Normalized`] ? classes.noNormalizedAddress : '';
  const message = loading.principalLoading ? WAITING_OPTION_LABELS : NO_OPTION_LABELS;
  const components = {
    Control: Control(inputComponent),
    Option,
    Placeholder
  };

  const noOptionsMessage = props => NoOptionsMessage({ ...props, text: message });

  return getSelectField({
    ...props,
    handleBlur: newHandleBlur,
    isClearable: false,
    extraClasses,
    components,
    noOptionsMessage
  });
};

export const getCommuneField = props => {
  const { handleBlur, setFieldValue, prefix, values, classes, loading } = props;
  const newHandleBlur = event => {
    const { value } = event.target;
    handleBlur(event);
    setFieldValue(`${prefix}${ADDRESS_NORMALIZE_PREFIX}`, value);
  };
  const extraClasses = !values[`is${prefix}Normalized`] ? classes.noNormalizedAddress : '';
  let message = loading.principalLoading ? WAITING_OPTION_LABELS : NO_OPTION_LABELS;
  message = values[`${prefix}Region`] ? message : SELECT_REGION_COMMUNE_SELECTOR;

  const components = {
    Control: Control(inputComponent),
    Option,
    Placeholder
  };
  const noOptionsMessage = props => NoOptionsMessage({ ...props, text: message });
  return getSelectField({
    ...props,
    handleBlur: newHandleBlur,
    isClearable: false,
    extraClasses,
    components,
    noOptionsMessage
  });
};

export const getAddressField = props => {
  const { handleBlur, setFieldValue, prefix, values, classes } = props;
  const newHandleBlur = event => {
    const { value } = event.target;
    handleBlur(event);
    setFieldValue(`${prefix}${ADDRESS_NORMALIZE_PREFIX}`, `${value}${event.timeStamp}`);
  };
  const changeHandler = value => {
    return maximumSubString(MAXIMUM_ADDRESS_LENGTH)(value);
  };

  const extraClasses = !values[`is${prefix}Normalized`] ? classes.noNormalizedAddress : '';
  return getInputField({ ...props, handleBlur: newHandleBlur, extraClasses, changeHandler });
};

export const getAddressNumberField = props => {
  const {
    classes,
    touched,
    errors,
    values,
    setFieldValue,
    handleBlur,
    name,
    label,
    prefix,
    required = true
  } = props;
  const formControlProps = {};
  if (required) {
    formControlProps.required = true;
  }
  const extraClasses = !values[`is${prefix}Normalized`] ? classes.noNormalizedAddress : '';
  const changeHandler = value => {
    return maximumSubString(MAXIMUM_ADDRESS_NUMBER_LENGTH)(value);
  };
  return (
    <FormControl
      {...formControlProps}
      className={classNames(classes.quarterWidth, extraClasses, {
        [classes.error]: errors[name] && touched[name]
      })}
    >
      <InputLabel shrink={true} htmlFor={`${name}`}>
        {label}
      </InputLabel>
      <NumberFormat
        id={`${name}`}
        value={values[name]}
        onBlur={event => {
          const { value } = event.target;
          handleBlur(event);
          setFieldValue(`${prefix}${ADDRESS_NORMALIZE_PREFIX}`, `${value}${event.timeStamp}`);
        }}
        onKeyDown={event => {
          if (event.target.value.startsWith('0')) {
            event.target.value = event.target.value.replace('0', '');
          }
          return event;
        }}
        thousandSeparator={''}
        decimalSeparator={false}
        decimalScale={false}
        allowNegative={false}
        customInput={Input}
        onValueChange={values => {
          let { value } = values;
          value = changeHandler(value);
          setFieldValue(name, value);
        }}
      />
      <ErrorMessage name={`${name}`} component="div" className={classes.errorMessages} />
    </FormControl>
  );
};
export const getAttachedField = props => {
  const { classes, handleBlur, prefix, setFieldValue } = props;
  const formControlClass = classes.quarterWidth;
  const required = false;
  const newHandleBlur = event => {
    const { value } = event.target;
    handleBlur(event);
    setFieldValue(`${prefix}${ADDRESS_NORMALIZE_PREFIX}`, `${value}${event.timeStamp}`);
  };
  return getInputField({ ...props, formControlClass, required, handleBlur: newHandleBlur });
};

export const getEmailField = props => {
  const changeHandler = value => {
    return maximumSubString(MAXIMUM_EMAIL_LENGTH)(value);
  };
  return getInputField({ ...props, changeHandler });
};

export const getPhoneField = props => {
  const { classes, prefix, name, setFieldValue, handleBlur, values } = props;
  const mask = '_';
  const format = '+56 ##### ####';
  const formControlClass = classes.quarterWidth;
  const allowEmptyFormatting = true;
  const newHandleBlur = event => {
    setFieldValue(`${prefix}${name}`, values[name]);
    handleBlur(event);
  };
  return getFormatNumber({
    ...props,
    formControlClass,
    mask,
    format,
    allowEmptyFormatting,
    handleBlur: newHandleBlur
  });
};

export const getAllowEndorsementField = props => {
  const { classes } = props;
  return (
    <FormControl className={classNames(classes.fullWidth, classes.notMargin, classes.flexEnd, {})}>
      {getCheckBoxField({ ...props, labelPlacement: 'start' })}
    </FormControl>
  );
};

export const getHealthPlanSectionField = props => {
  const optionLabel = option => `${option.range}`;
  const optionValue = option => option.range;
  return getSelectField({ ...props, optionLabel, optionValue });
};

export const getInsuranceField = props => {
  const optionValue = option => option.name.toUpperCase();
  return getSelectField({ ...props, optionValue });
};

export const getSameSuscriptorField = props => {
  const { classes, values } = props;
  const prefix = 'patient';
  if (values[`${prefix}SuscriptorCb`]) {
    checkInPatient({ ...props, prefix });
  }

  const disabled = !values['suscriptorDniNumber'];

  return (
    <FormControl className={classNames(classes.quarterCBWidth, {})}>
      {getCheckBoxField({ ...props, labelPlacement: 'end', disabled })}
    </FormControl>
  );
};

export const getOncologicalAgreementField = props => {
  const { classes } = props;
  return (
    <FormControl className={classNames(classes.quarterCBWidth, {})}>
      {getCheckBoxField({ ...props, labelPlacement: 'end' })}
    </FormControl>
  );
};

export const getComplementaryInsuranceField = props => {
  const { classes } = props;
  return (
    <FormControl className={classNames(classes.quarterCBWidth, {})}>
      {getCheckBoxField({ ...props, labelPlacement: 'end' })}
    </FormControl>
  );
};
